import { Menu, MenuItem } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useRef, useState, useEffect, Key } from "react";

import { TextFieldControl } from "./TextFieldControl";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

interface SuggestionSelectOptions {
  initialValue?: string;
  placeholder?: string;
  more_info?: any;
  multiline?: boolean;
  info?: any;
  redact_summary?: boolean;
  suggestions?: string[];
}

export type SuggestionSelectProps = CascadiaControlProps & {
  options: SuggestionSelectOptions;
  isLoading?: boolean;
};

export function SuggestionSelectControl(props: SuggestionSelectProps) {
  const { options, isLoading = false } = props;
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (value: string) => {
    props.setValue(value);

    if (isLoading || !options.suggestions || !value.trim()) {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
      return;
    }

    const uniqueSuggestions = Array.from(new Set(options.suggestions));
    const filtered = uniqueSuggestions.filter((sugg) =>
      sugg.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  };

  const handleSuggestionClick = (key: Key) => {
    const suggestion = key.toString();
    props.setValue(suggestion);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    props.setTouched();
    setTimeout(() => setShowSuggestions(false), 100);
  };

  const textFieldProps = {
    ...props,
    setValue: handleChange,
    options: {
      ...options,
      onBlur: handleBlur,
    },
  };

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div ref={inputRef}>
        <TextFieldControl {...textFieldProps} />
      </div>
      {showSuggestions && (
        <div className={styles.dropdown} data-testid="suggestion-popover">
          {isLoading ? (
            <div className={styles.loading} data-testid="suggestion-loading">
              Loading suggestions...
            </div>
          ) : filteredSuggestions.length > 0 ? (
            <Menu onItemAction={handleSuggestionClick}>
              {filteredSuggestions.map((sugg) => (
                <MenuItem
                  key={sugg}
                  id={sugg}
                  textValue={sugg}
                  data-testid={`suggestion-item-${sugg}`}
                >
                  {sugg}
                </MenuItem>
              ))}
            </Menu>
          ) : null}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: css({
    position: "relative",
  }),
  dropdown: css({
    position: "absolute",
    backgroundColor: "var(--surface-overlay)",
    borderRadius: "var(--border-radius-2)",
    display: "flex",
    flexDirection: "column",
    boxShadow: "var(--shadow-menu)",
    top: "calc(100% - 18px)",
    left: 0,
    right: 0,
    zIndex: 50,
    maxHeight: "220px",
    overflow: "auto",
  }),
  loading: css({
    padding: "12px 16px",
    color: "#6b7280",
  }),
};

SuggestionSelectControl.displayName = "SuggestionSelectControl";

export const SuggestionSelectControlRenderer = withCascadiaControlProps(
  SuggestionSelectControl
);
